/**
 * Algoritmo de Repetição Espaçada SuperMemo 2 (SM-2) Avançado
 * Utilizado exclusivamente pelo ecossistema Flashcards 2.0 da MedExperts / Veri Salvatori.
 */

/**
 * Calcula o próximo estado de retenção de um flashcard com base na nota do aluno.
 * 
 * @param {number} grade - Nota de 1 a 5 atribuída pelo aluno:
 *   1: Muito difícil (Blackout / Erro grave)
 *   3: Difícil (Lembrou com grande esforço)
 *   4: Fácil (Lembrou com pequena hesitação)
 *   5: Muito fácil (Resposta perfeita e imediata)
 * @param {object} previous - Estado anterior do card no SM-2
 *   previous.repetitions: número de repetições bem-sucedidas consecutivas (default: 0)
 *   previous.easiness: fator de facilidade EF (default: 2.5, min: 1.3)
 *   previous.interval: intervalo atual em dias (default: 0)
 * @returns {object} Novo estado { repetitions, easiness, interval, nextReviewDate, lastReviewed, grade }
 */
export function calculateSM2(grade, previous = {}) {
  const safeGrade = Number(grade) || 0;
  let repetitions = Number(previous.repetitions) || 0;
  let interval = Number(previous.interval) || 0;
  let easiness = Number(previous.easiness) || 2.5;

  // Fórmula padrão SM-2: EF' = EF + (0.1 - (5 - grade) * (0.08 + (5 - grade) * 0.02))
  let newEasiness = easiness + (0.1 - (5 - safeGrade) * (0.08 + (5 - safeGrade) * 0.02));
  if (newEasiness < 1.3) newEasiness = 1.3;
  if (newEasiness > 3.5) newEasiness = 3.5;

  // Se safeGrade >= 3, o card foi lembrado com sucesso
  if (safeGrade >= 3) {
    if (repetitions === 0) {
      interval = 1; // 1º acerto consecutivo: intervalo de 1 dia
    } else if (repetitions === 1) {
      interval = 6; // 2º acerto consecutivo: intervalo de 6 dias
    } else {
      const baseInterval = interval > 0 ? interval : 6;
      interval = Math.round(baseInterval * newEasiness); // 3º em diante: baseInterval * EF
    }
    repetitions += 1;
  } else {
    // Erro / Muito difícil (safeGrade < 3): reinicia contagem de repetições consecutivas
    repetitions = 0;
    interval = 1; // Revisa amanhã
  }

  const now = new Date();
  const nextDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + interval);

  const year = nextDate.getFullYear();
  const month = String(nextDate.getMonth() + 1).padStart(2, '0');
  const day = String(nextDate.getDate()).padStart(2, '0');
  const nextReviewDate = `${year}-${month}-${day}`;

  return {
    repetitions,
    easiness: parseFloat(newEasiness.toFixed(2)),
    interval,
    lastReviewed: now.toISOString(),
    nextReviewDate,
    grade: safeGrade
  };
}

/**
 * Retorna a data de hoje no formato YYYY-MM-DD (fuso horário local)
 */
export function getTodayDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Extrai a data no formato YYYY-MM-DD de forma segura contra fusos e diferentes formatos
 * @param {string|Date} dateVal 
 * @returns {string|null}
 */
export function normalizeDateToLocalDateString(dateVal) {
  if (!dateVal) return null;
  if (typeof dateVal === 'string') {
    // Formato brasileiro DD/MM/YYYY
    if (dateVal.includes('/')) {
      const parts = dateVal.split('/');
      if (parts.length === 3) {
        const d = parts[0].padStart(2, '0');
        const m = parts[1].padStart(2, '0');
        const y = parts[2];
        return `${y}-${m}-${d}`;
      }
    }
    // Formato ISO simples YYYY-MM-DD sem tempo
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal.trim())) {
      return dateVal.trim();
    }
  }

  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Avalia se um card está devido para revisão hoje ou em atraso.
 * Se o card já foi estudado/revisado hoje, ele não é considerado devido hoje.
 * 
 * @param {object} sm2Record - Registro do card em difficultyTrackingV2
 * @param {string} today - Data de referência YYYY-MM-DD
 * @returns {boolean}
 */
export function isCardDue(sm2Record, today = getTodayDateString()) {
  if (!sm2Record || !sm2Record.nextReviewDate) return false;

  // Se o card já foi respondido/revisado hoje na data local, ele já foi concluído para hoje
  if (sm2Record.lastReviewed) {
    const lastReviewedDateStr = normalizeDateToLocalDateString(sm2Record.lastReviewed);
    if (lastReviewedDateStr === today) {
      return false;
    }
  }

  const nextReviewDateStr = normalizeDateToLocalDateString(sm2Record.nextReviewDate);
  if (!nextReviewDateStr) return false;

  return nextReviewDateStr <= today;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { calculateSM2, getTodayDateString, normalizeDateToLocalDateString, isCardDue };
}
